import { createContext, useContext, useEffect, useState, type ReactNode, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api";
import { Alert, Button, FormError, TextInput } from "./ui";

export interface User { id: string; email: string; name: string; role: "owner" | "staff" }
interface AuthState { user: User | null; signOut: () => Promise<void>; refresh: () => Promise<void> }
const Ctx = createContext<AuthState>({ user: null, signOut: async () => {}, refresh: async () => {} });
export const useAuth = () => useContext(Ctx);
export const useIsOwner = () => useAuth().user?.role === "owner";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ loading: boolean; user: User | null; setupNeeded: boolean; error: unknown }>({ loading: true, user: null, setupNeeded: false, error: null });
  const qc = useQueryClient();

  const refresh = async () => {
    try {
      const me = await api.get<{ user: User }>("/me");
      setState({ loading: false, user: me.user, setupNeeded: false, error: null });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        try {
          const s = await api.get<{ needed: boolean }>("/auth/setup");
          setState({ loading: false, user: null, setupNeeded: s.needed, error: null });
        } catch (e2) { setState({ loading: false, user: null, setupNeeded: false, error: e2 }); }
      } else setState({ loading: false, user: null, setupNeeded: false, error: e });
    }
  };

  useEffect(() => {
    refresh();
    const onExpired = () => { qc.clear(); setState((s) => ({ ...s, user: null })); };
    window.addEventListener("wgm:unauthenticated", onExpired);
    return () => window.removeEventListener("wgm:unauthenticated", onExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = async () => {
    try { await api.post("/auth/logout"); } finally { qc.clear(); setState({ loading: false, user: null, setupNeeded: false, error: null }); }
  };

  if (state.loading) return <div className="auth-wrap"><p className="muted">Loading…</p></div>;
  if (state.error) return <AuthFrame><Alert kind="error" title="Can't reach the server">{state.error instanceof Error ? state.error.message : "Unknown error"}</Alert><Button onClick={refresh} style={{ marginTop: 12 }}>Try again</Button></AuthFrame>;
  if (!state.user) return state.setupNeeded ? <SetupPage onDone={refresh} /> : <LoginPage onDone={refresh} />;
  return <Ctx.Provider value={{ user: state.user, signOut, refresh }}>{children}</Ctx.Provider>;
}

function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 16, background: "linear-gradient(160deg, #0a1f44, #102a5c)" }}>
      <div className="card" style={{ width: "100%", maxWidth: 420, padding: 24 }}>
        <img src="/logo.png" alt="Wali Autofix Ltd" style={{ width: "100%", height: "auto", marginBottom: 16 }} />
        {children}
      </div>
    </div>
  );
}

function LoginPage({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [challenge, setChallenge] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ twoStepRequired?: boolean; challenge?: string }>("/auth/login", { email, password });
      if (r.twoStepRequired && r.challenge) { setPassword(""); setChallenge(r.challenge); } else onDone();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  if (challenge) return <TwoStepPage challenge={challenge} onDone={onDone} onRestart={() => { setChallenge(null); setError(null); }} />;
  return (
    <AuthFrame>
      <h1 style={{ marginBottom: 4 }}>Sign in</h1>
      <p className="muted" style={{ marginTop: 0 }}>Garage Manager</p>
      <form onSubmit={submit} className="stack">
        <TextInput label="Email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextInput label="Password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        {error ? <FormError error={error} /> : null}
        <Button type="submit" variant="primary" loading={busy} style={{ width: "100%" }}>Sign in</Button>
      </form>
    </AuthFrame>
  );
}

/** Second step: the 6-digit code from the authenticator app, or one recovery code. */
function TwoStepPage({ challenge, onDone, onRestart }: { challenge: string; onDone: () => void; onRestart: () => void }) {
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post("/auth/login/two-step", useRecovery ? { challenge, recoveryCode: code } : { challenge, code: code.replace(/\s/g, "") });
      onDone();
    } catch (err) {
      setError(err);
      if (err instanceof ApiError && err.code === "two_step_expired") onRestart();
    } finally { setBusy(false); }
  };
  return (
    <AuthFrame>
      <h1 style={{ marginBottom: 4 }}>Two-step sign-in</h1>
      <p className="muted" style={{ marginTop: 0 }}>{useRecovery ? "Enter one of your recovery codes. Each code works once." : "Open your authenticator app and enter the 6-digit code for Wali Garage Manager."}</p>
      <form onSubmit={submit} className="stack">
        {useRecovery
          ? <TextInput label="Recovery code" autoComplete="off" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="xxxx-xxxx-xx" />
          : <TextInput label="6-digit code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required autoFocus value={code} onChange={(e) => setCode(e.target.value)} />}
        {error ? <FormError error={error} /> : null}
        <Button type="submit" variant="primary" loading={busy} style={{ width: "100%" }}>Continue</Button>
        <Button type="button" variant="ghost" onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(null); }}>{useRecovery ? "Use the authenticator app instead" : "Lost your phone? Use a recovery code"}</Button>
        <Button type="button" variant="ghost" onClick={onRestart}>Start again</Button>
      </form>
    </AuthFrame>
  );
}

function SetupPage({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ name: "", email: "", password: "", setupToken: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await api.post("/auth/setup", f); onDone(); } catch (err) { setError(err); } finally { setBusy(false); }
  };
  return (
    <AuthFrame>
      <h1 style={{ marginBottom: 4 }}>Create the owner account</h1>
      <p className="muted" style={{ marginTop: 0 }}>First-time setup. This can only be done once.</p>
      <form onSubmit={submit} className="stack">
        <TextInput label="Your name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <TextInput label="Email" type="email" required autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <TextInput label="Password" type="password" required autoComplete="new-password" hint="At least 10 characters." value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <TextInput label="Setup token" hint="Required on staging/production (set as a Cloudflare secret). Leave blank for local development." value={f.setupToken} onChange={(e) => setF({ ...f, setupToken: e.target.value })} />
        {error ? <FormError error={error} /> : null}
        <Button type="submit" variant="primary" loading={busy} style={{ width: "100%" }}>Create account</Button>
      </form>
    </AuthFrame>
  );
}
